// Wave-15-prep Day 4 — Shared cross-shell NotificationBell (extracted from
// features/admin/ per audit P0 finding: "Seller has no notification surface
// at all — NotificationBell only mounted in AdminShell").
//
// Single bell component shared by admin / seller / supply shells. Shell prop
// switches:
//   - fetch URL (/admin/notifications | /seller/notifications | /supply/notifications)
//   - deep-link route map per shell (admin→/admin/po/$id, seller→/seller/inventory, etc.)
//   - notification title/description render (seller-leak scrub: vendor identity NEVER
//     surfaced on seller side per feedback_seller_surface_no_supplier_leak invariant)
//
// V1 simplifications (operational simulation discipline, frontend-first mode):
//   - Mark-as-read state is in-memory only (module-level Set, keyed by shell);
//     resets on reload.
//   - Desktop keeps the compact anchored dropdown; mobile uses a modal drawer
//     with backdrop/Escape dismissal and focus restoration.
//   - Polling: refetch every 60s via TanStack Query refetchInterval.
//
// D-7 surface segregation: each shell sees only its own slice. Admin does NOT
// see seller-only events here; seller does NOT see admin PO lifecycle events.

import { useMemo, useState, useSyncExternalStore } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { PexIcon } from './icons/PexIcon'
import { apiUrl } from '../lib/api'
import { formatRelative } from '../lib/datetime'
import { useT } from '../i18n'
import { notificationBellI18n } from '../i18n/shared/notification-bell'
import {
  listTaskNotifications,
  markTaskNotificationsRead,
  staffTaskKeys,
  type TaskNotification,
} from '../features/staff-tasks/api'
import { taskDetailRoute } from '../features/staff-tasks/lib'

export type NotificationShell = 'admin' | 'seller' | 'supply'

type NotifEvent = {
  id: string
  type: string
  product_id: string
  seller_org_id: string
  qty: number
  wallet_delta_usd: number
  timestamp: string
  metadata?: Record<string, unknown>
}

type NotificationsResponse = {
  events: NotifEvent[]
  unread_count: number
  meta: { total: number; since: string }
}

// Module-level read-state. Keyed per shell so admin's reads don't shadow
// seller's badge count and vice versa.
const readEventIdsByShell: Record<NotificationShell, Set<string>> = {
  admin: new Set<string>(),
  seller: new Set<string>(),
  supply: new Set<string>(),
}

// Forbidden tokens that must NEVER appear in seller-side notification copy.
// Defense-in-depth: even if a handler payload leaks vendor identity via
// metadata.vendor_name or product description, the renderer scrubs it before
// the seller sees anything. Mirrors feedback_seller_surface_no_supplier_leak.
const SELLER_LEAK_TOKENS = /\b(Mehmet|Hakan|Aybars|Workshop|Üretici|Producer|Bursa|Izmir|İzmir)\b/gi

function scrubSellerLeak(text: string, replacementToken: string): string {
  return text.replace(SELLER_LEAK_TOKENS, replacementToken)
}

type EventTitleDict = (typeof notificationBellI18n)['en']['eventTitles']

function eventTitle(
  e: NotifEvent,
  shell: NotificationShell,
  titles: EventTitleDict,
): string {
  switch (e.type) {
    case 'po.acknowledged':
      return titles.poAcknowledged
    case 'po.in_transit':
      return titles.poInTransit
    case 'po.received':
      return titles.poReceived
    case 'po.disputed':
      return titles.poDisputed
    case 'po.closed':
      return titles.poClosed
    case 'po.cancelled':
      return titles.poCancelled
    case 'po.inbox_arrival':
      return titles.poInboxArrival
    case 'receiving.qc_quarantined':
      return titles.receivingQcQuarantined
    case 'reorder.threshold_breached':
      return titles.reorderThresholdBreached
    case 'inventory.real_out_of_stock':
      return titles.inventoryRealOutOfStock
    case 'allocation.released_by_seller':
      return shell === 'seller'
        ? titles.allocationReleasedBySellerSelf
        : titles.allocationReleasedBySellerAdmin
    case 'orders.return_requested':
      return titles.ordersReturnRequested
    case 'orders.has_issues':
      return titles.ordersHasIssues
    case 'etsy_listing_push.uncertain_reminder':
      return titles.etsyPushUncertainReminder
    case 'vendor_replacement.affecting_listed_product':
      // Seller-side wording: abstract, never names the vendor.
      return shell === 'seller'
        ? titles.vendorReplacementAffectingSeller
        : titles.vendorReplacementAffectingAdmin
    case 'work_order.ack_overdue':
      return titles.workOrderAckOverdue
    case 'work_order.deadline_approaching':
      return titles.workOrderDeadlineApproaching
    case 'qc.disposition_pending':
      return titles.qcDispositionPending
    case 'etsy_push.stale_tracking_after_replacement':
      return titles.etsyTrackingStaleAfterReplacement
    case 'ebay_push.stale_tracking_after_void':
      return titles.ebayTrackingStaleAfterVoid
    case 'shopify_push.stale_tracking_after_void':
      return titles.shopifyTrackingStaleAfterVoid
    case 'shipping.channel_push_snapshot_unreconciled':
      return titles.channelPushSnapshotUnreconciled
    default:
      return e.type
  }
}

function eventDescription(
  e: NotifEvent,
  shell: NotificationShell,
  scrubToken: string,
): string {
  const md = e.metadata ?? {}
  // Admin shell may render vendor_name from PO metadata; seller MUST NOT.
  if (typeof md.po_id === 'string') {
    return `${md.po_id} · ${e.product_id}`
  }
  if (typeof md.stale_tracking_number === 'string') {
    return typeof md.new_tracking_number === 'string'
      ? `${md.stale_tracking_number} → ${md.new_tracking_number}`
      : md.stale_tracking_number
  }
  if (typeof md.product_name === 'string') {
    const name = String(md.product_name)
    return shell === 'seller' ? scrubSellerLeak(name, scrubToken) : name
  }
  return e.product_id
}

type EventLinkTarget = {
  to: string
  params?: Record<string, string>
}

// Marketplace tracking pushes (Etsy/eBay/Shopify) and the void-side
// reconciliation warning all live on the order's shipping panel.
function isOrderTrackingEvent(type: string): boolean {
  return (
    type.startsWith('etsy_push.') ||
    type.startsWith('ebay_push.') ||
    type.startsWith('shopify_push.') ||
    type === 'shipping.channel_push_snapshot_unreconciled'
  )
}

// Deep-link map per shell. Each shell only knows its own routes; cross-shell
// navigation is a D-7 violation and forbidden here.
function getEventLink(e: NotifEvent, shell: NotificationShell): EventLinkTarget {
  const md = e.metadata ?? {}
  const poId = typeof md.po_id === 'string' ? md.po_id : null
  const woId = typeof md.work_order_id === 'string' ? md.work_order_id : null
  const orderId = typeof md.order_id === 'string' ? md.order_id : null

  if (shell === 'admin') {
    if (e.type.startsWith('po.') && poId) return { to: '/admin/po/$id', params: { id: poId } }
    if (e.type.startsWith('receiving.') && poId)
      return { to: '/admin/po/$id', params: { id: poId } }
    if (isOrderTrackingEvent(e.type) && orderId)
      return { to: '/admin/orders/$id', params: { id: orderId } }
    if (isOrderTrackingEvent(e.type)) return { to: '/admin/orders' }
    // reorder.* + inventory.* + allocation.* surface in the inventory products list.
    return { to: '/admin/inventory/products' }
  }

  if (shell === 'seller') {
    // Sellers never deep-link into PO detail (admin-owned surface). All seller
    // events route to inventory + orders surfaces they own.
    if (e.type.startsWith('orders.')) return { to: '/seller/my-orders' }
    if (e.type === 'vendor_replacement.affecting_listed_product')
      return { to: '/seller/my-products' }
    if (e.type === 'etsy_listing_push.uncertain_reminder' && typeof md.listing_id === 'string')
      return { to: '/seller/listings/$id', params: { id: md.listing_id } }
    return { to: '/seller/inventory' }
  }

  // supply shell
  if (e.type.startsWith('work_order.') && woId)
    return { to: '/supply/work-orders/$id', params: { id: woId } }
  if (e.type === 'po.inbox_arrival') return { to: '/supply/po-inbox' }
  if (e.type === 'qc.disposition_pending') return { to: '/supply/work-orders' }
  return { to: '/supply' }
}

const FETCH_URL_BY_SHELL: Record<NotificationShell, string> = {
  admin: '/admin/notifications',
  seller: '/seller/notifications',
  supply: '/supply/notifications',
}

const MOBILE_QUERY = '(max-width: 767px)'

function subscribeToMobileViewport(onStoreChange: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined
  const media = window.matchMedia(MOBILE_QUERY)
  media.addEventListener('change', onStoreChange)
  return () => media.removeEventListener('change', onStoreChange)
}

function getMobileViewportSnapshot() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia(MOBILE_QUERY).matches
}

export type NotificationBellProps = {
  shell?: NotificationShell
  /** Trigger color tone: 'light' for white surfaces, 'dark' for the slate Topbar. */
  tone?: 'light' | 'dark'
}

export function NotificationBell({
  shell = 'admin',
  tone = 'light',
}: NotificationBellProps = {}) {
  const [open, setOpen] = useState(false)
  const isMobile = useSyncExternalStore(
    subscribeToMobileViewport,
    getMobileViewportSnapshot,
    () => false,
  )
  // tick state — bumped on mark-as-read so the badge re-renders against the
  // module-level Set without forcing a refetch.
  const [, setTick] = useState(0)
  // 'mine' = the caller's own task notifications (assigned, mentioned,
  // commented, overdue); 'system' = the shell's audit-event feed below.
  const [tab, setTab] = useState<'mine' | 'system'>('mine')
  const queryClient = useQueryClient()

  const t = useT(notificationBellI18n)
  const fetchPath = FETCH_URL_BY_SHELL[shell]
  const readEventIds = readEventIdsByShell[shell]

  const query = useQuery<NotificationsResponse>({
    queryKey: [shell, 'notifications'],
    queryFn: async () => {
      const res = await fetch(apiUrl(fetchPath))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()) as NotificationsResponse
    },
    refetchInterval: 60_000,
  })

  const events = useMemo(() => query.data?.events ?? [], [query.data?.events])
  const unreadCount = useMemo(
    () => events.filter((e) => !readEventIds.has(e.id)).length,
    // events array reference changes on refetch; tick re-evaluates after mark-as-read.
    [events, readEventIds],
  )
  const visible = events.slice(0, 10)

  const mineQuery = useQuery({
    queryKey: staffTaskKeys.notifications,
    queryFn: listTaskNotifications,
    refetchInterval: 60_000,
    retry: false,
  })
  // No task capability (or no team): the bell stays the system feed only.
  const hasMine = !mineQuery.isError
  const mine = mineQuery.data?.items ?? []
  const mineUnread = mineQuery.data?.unread_count ?? 0
  const markMine = useMutation({
    mutationFn: markTaskNotificationsRead,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.notifications }),
  })
  const activeTab = hasMine ? tab : 'system'
  const badgeCount = unreadCount + (hasMine ? mineUnread : 0)

  function markRead(id: string): void {
    readEventIds.add(id)
    setTick((n) => n + 1)
  }

  const notificationList = (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {visible.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm italic text-slate-500">
          {t.emptyState}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {visible.map((e) => {
            const link = getEventLink(e, shell)
            const isRead = readEventIds.has(e.id)
            return (
              <li key={e.id}>
                <Link
                  to={link.to}
                  params={link.params}
                  onClick={() => {
                    markRead(e.id)
                    setOpen(false)
                  }}
                  data-testid={`notification-event-link-${e.id}`}
                  className={`block px-4 py-3 transition-colors hover:bg-slate-50 ${isRead ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {eventTitle(e, shell, t.eventTitles)}
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {eventDescription(e, shell, t.pexifyScrubToken)}
                      </div>
                    </div>
                    <div className="shrink-0 text-[11px] tabular-nums text-slate-400">
                      {formatRelative(e.timestamp)}
                    </div>
                  </div>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )

  const mineList = (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {mine.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm italic text-slate-500">{t.mineEmpty}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {mine.map((n: TaskNotification) => {
            const row = (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div
                    className={`truncate text-sm text-slate-900 ${n.is_read ? '' : 'font-semibold'}`}
                  >
                    {n.title}
                  </div>
                  {n.body && <div className="truncate text-xs text-slate-500">{n.body}</div>}
                </div>
                <div className="shrink-0 text-[11px] tabular-nums text-slate-400">
                  {formatRelative(n.created_at)}
                </div>
              </div>
            )
            const onOpen = () => {
              if (!n.is_read) markMine.mutate([n.id])
              setOpen(false)
            }
            return (
              <li key={n.id}>
                {n.source_entity_id ? (
                  <Link
                    {...taskDetailRoute(shell, n.source_entity_id)}
                    onClick={onOpen}
                    data-testid={`notification-mine-link-${n.id}`}
                    className={`block px-4 py-3 transition-colors hover:bg-slate-50 ${n.is_read ? 'opacity-60' : ''}`}
                  >
                    {row}
                  </Link>
                ) : (
                  <div className="px-4 py-3">{row}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )

  const tabBar = hasMine ? (
    <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-1.5" role="tablist">
      {(['mine', 'system'] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={activeTab === value}
          onClick={() => setTab(value)}
          className={`rounded-md px-2.5 py-1 text-xs font-medium ${activeTab === value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
        >
          {value === 'mine' ? t.tabMine : t.tabSystem}
          {value === 'mine' && mineUnread > 0 ? ` · ${mineUnread}` : ''}
        </button>
      ))}
      {activeTab === 'mine' && mineUnread > 0 ? (
        <button
          type="button"
          onClick={() => markMine.mutate(undefined)}
          className="ml-auto text-xs font-medium text-sky-700 hover:underline"
        >
          {t.markAllRead}
        </button>
      ) : null}
    </div>
  ) : null

  const body = (
    <>
      {tabBar}
      {activeTab === 'mine' ? mineList : notificationList}
    </>
  )

  function bellButton(onClick?: () => void) {
    return (
      <button
        type="button"
        aria-label={t.bellAriaLabel}
        aria-expanded={open}
        onClick={onClick}
        className={
          'relative inline-flex h-11 w-11 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 md:h-9 md:w-9 ' +
          (tone === 'dark'
            ? 'text-slate-300 hover:bg-slate-800 hover:text-white focus-visible:ring-slate-500'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-400')
        }
      >
        <PexIcon name="notifications" size="md" tone="inherit" />
        {badgeCount > 0 ? (
          <span
            data-testid="notification-bell-badge"
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        ) : null}
      </button>
    )
  }

  if (isMobile) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Trigger asChild>{bellButton()}</DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            data-testid="notification-mobile-backdrop"
            onPointerDown={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-slate-950/35 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none"
          />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            aria-modal="true"
            className="fixed inset-y-0 right-0 z-[71] flex h-[100dvh] w-full max-w-[90vw] flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right motion-reduce:animate-none"
          >
            <header className="flex min-h-16 shrink-0 items-center justify-between border-b border-slate-100 px-4 pt-[env(safe-area-inset-top)]">
              <div>
                <DialogPrimitive.Title className="text-base font-semibold text-slate-900">
                  {t.headerTitle}
                </DialogPrimitive.Title>
                <div className="text-xs text-slate-500">
                  {events.length} {t.recentSuffix}
                </div>
              </div>
              <DialogPrimitive.Close
                type="button"
                aria-label={t.closeAriaLabel}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              >
                <PexIcon name="close" size="md" tone="inherit" />
              </DialogPrimitive.Close>
            </header>
            {body}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    )
  }

  return (
    <div className="relative">
      {bellButton(() => setOpen((v) => !v))}

      {open ? (
        <div className="absolute right-0 z-40 mt-2 w-96 origin-top-right rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
            <div className="text-sm font-semibold text-slate-900">{t.headerTitle}</div>
            <div className="text-xs text-slate-500">
              {events.length} {t.recentSuffix}
            </div>
          </div>
          {tabBar}
          <div className="max-h-96 overflow-auto">
            {activeTab === 'mine' ? mineList : notificationList}
          </div>
        </div>
      ) : null}
    </div>
  )
}
